import join from 'url-join';
import { push } from 'connected-react-router';
import { asyncAction, createAction } from '../util';
import { showToast } from '../components/toast/actions';
import { selectShopDepartment } from '../shop/actions';
import { getUserAccount, getUserData } from '../user/selectors';
import { TranslatedCopy } from '../intl/TranslatedCopy';
import { DEFAULT_CART_PRODUCT_STRUCTURE } from './util';
import { ZERO_DOLLAR_VALUE } from '../util/constants';
import {
  getCurrentCart,
  orderTypeToCartType,
  cartIsLoading,
  getProductGroupByType,
  getCurrentCartEtag,
  shippingMethodsMap,
  hasErrors,
  paymentTypeToCartProductPaymentType,
  ADD_TO_CART_ERROR_MESSAGE
} from './constants';
import { fetchGamedayAccount } from '../gamedayAccount/actions';
import { ORDER_TYPE_ATONCE, paymentMethodsMap } from '../shop/constants';
import { getSelectedDepartment } from '../shop/selectors';
import { getCurrentAllotments, getFutureAllotments } from '../gamedayAccount/selectors';
import { mapAllotmentDepts } from '../util/mapAllotmentDepts';
import { hasSubmittedToggle } from '../checkout/actions';
import { mergedAddresses } from '../addressBook/selectors';
import { requestAddressById } from '../addressBook/actions';
import { fetchProductInfo, getProductInfo } from '../productsInfo';
import { openModal, closeModal } from '../components/modal/actions';
import http from '../util/http';
import { flatten } from '../util/array/flatten';
import { ADDRESS_TYPES, getProductAddressInfo } from '../addressBook/constants';
import {
  sortAndFilterCart,
  setCurrentCartTotals,
  setPaymentMethod,
  setProductAddress,
  updateCartProductPurchasables,
  validateCart
} from './actionCreators';

const api = join(import.meta.env.VITE_API_URL, 'v1');
const api2 = join(import.meta.env.VITE_API2_URL, 'v1');
const defaultPaymentMethod = { server: 'COMP-CASH', frontend: 'COMP' };

export const createApiSearchUrl = (path, filterParams = {}) => {
  const filterQuery = Object.entries(filterParams).reduce((acc, [key, value], i) => {
    const delimiter = i === 0 ? '?' : '&';
    const filter = `filter=${key}(${value})`;
    return `${acc}${delimiter}${filter}`;
  }, '');
  const sortQuery = `${filterQuery ? '&' : '?'}sort=createTimestampDesc`;
  const query = filterQuery + sortQuery;
  return join(api2, path, query);
};

const getProductsStyleCodes = products =>
  products.map(({ styleColor = null, productCode = null }) =>
    styleColor ? styleColor.split('-')[0] : productCode.split('-')[0]
  );

export const getCustomAddressForCurrentCart = () => (dispatch, getState) => {
  const state = getState();
  const currentCart = getCurrentCart(state);
  const knownAddressIds = mergedAddresses(state).map(address => address.id);
  const unknownCustomAddresses = currentCart.products
    .filter(product => product.addressType === ADDRESS_TYPES.GAMEDAY && !knownAddressIds.includes(product.addressId))
    .map(address => address.addressId);
  const uniqueUnknownAddresses = [...new Set(unknownCustomAddresses)];

  return Promise.all(uniqueUnknownAddresses.map(addressId => dispatch(requestAddressById({ addressId }))));
};

/**
 * @name updateAccountCart
 * @async
 * @desc request update for cart
 * @param {object} params
 * @param {string} cartId the id of the cart to be updated
 */
export const updateAccountCart = asyncAction({
  baseName: 'UPDATE_ACCOUNT_CART',
  action: action => ({ cartId }) => dispatch => {
    dispatch(action.start({ cartId }));

    return http
      .get(join(api2, `carts/${cartId}`))
      .then(({ data: response }) => dispatch(action.success({ cart: response })))
      .catch(err => dispatch(action.error(err)));
  }
});

/**
 * @name addProductsRequest
 * @async
 * @desc request products from  api
 * @param {object} params
 * @param {string} params.cartId
 * @param {string} [params.addressId]
 * @param {array} params.product
 * @param {number} params.accountNumber
 * @param {string} [params.paymentMethod]
 */

export const addProductsRequest = asyncAction({
  baseName: 'ADD_TO_CART',
  action: action => ({ cartId, addressId, product, accountNumber, paymentMethod = defaultPaymentMethod.server }) => (
    dispatch,
    getState
  ) => {
    dispatch(action.start(cartId));
    /* eslint-disable no-unused-vars */
    const state = getState();
    const etag = getCurrentCartEtag(state);
    const headers = {
      // ...(etag && { 'If-Match': etag })
    };
    /* eslint-enable no-unused-vars */
    const sizes = product.sizes
      ? product.sizes
          .map(({ code = null, quantity = null, sizeDisplay = '' }) => {
            if (code && quantity) {
              return {
                sizeCode: code,
                quantity: +quantity,
                sizeDisplay
              };
            }
            return null;
          })
          .filter(v => v)
      : [];

    const params = {
      productCode: product.productCode,
      paymentMethod,
      fundamentalRuleId: product.fundamentalRuleId,
      ...(sizes.length > 0 && { sizes }),
      ...(addressId && { addressId })
    };

    return http
      .post(join(api2, `carts/${cartId}/products`), params, headers)
      .then(({ data: { id, customerRequestDate }, httpMeta }) => {
        const normalizedProduct = {
          ...DEFAULT_CART_PRODUCT_STRUCTURE,
          fundamentalRuleId: product.fundamentalRuleId,
          productCode: product.productCode,
          isPurchasable: true,
          id,
          customerRequestDate,
          wholesalePrice: product.prices[0].wholesale || 0,
          retailPrice: product.prices[0].retail || 0,
          paymentMethod: defaultPaymentMethod.frontend,
          ...(sizes.length > 0 && { sizes })
        };
        return dispatch(fetchProductInfo({ styleNumbers: [product.productCode.split('-')[0]] })).then(() =>
          dispatch(action.success({ accountNumber, cartId, product: normalizedProduct, cartProductId: id, httpMeta }))
        );
      })
      .catch(error => {
        dispatch(
          showToast({
            message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
            type: 'error',
            error
          })
        );
        return dispatch(action.error(error));
      });
  }
});

export const duplicateCartProductsRequest = asyncAction({
  baseName: 'DUPLICATE_CART_PRODUCTS',
  action: action => ({ cartId, addressId, product, paymentMethod = defaultPaymentMethod.server }) => dispatch => {
    dispatch(action.start({ cartProductId: product.id }));

    const params = {
      productCode: product.productCode,
      paymentMethod,
      fundamentalRuleId: product.fundamentalRuleId,
      ...(product.sizes.length > 0 && { sizes: product.sizes }),
      ...(addressId && { addressId })
    };

    return http
      .post(join(api2, `carts/${cartId}/products`), params)
      .then(({ data: { id }, httpMeta }) =>
        dispatch(
          action.success({
            product: {
              ...product,
              id
            },
            dupProductId: product.id,
            httpMeta
          })
        )
      )
      .catch(error => {
        dispatch(
          showToast({
            message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
            type: 'error',
            error
          })
        );
        return dispatch(action.error({ message: error?.message, cartProductId: product.id }));
      });
  }
});

export const getAllAccountCarts = asyncAction({
  baseName: 'GET_ALL_ACCOUNT_CARTS',
  action: action => (accountNumber, searchFilters = {}) => dispatch => {
    dispatch(action.start(accountNumber));
    const cartStatus = 'OPEN,PENDING_APPROVAL';
    const filters = {
      accountNumber,
      cartStatus,
      cartProductCountGreater: 0,
      ...searchFilters
    };

    return http
      .get(`${createApiSearchUrl('carts', filters)}&count=100`)
      .then(({ data: response }) => {
        const carts = response.objects.map(cart => ({
          ...cart,
          accountNumber: String(accountNumber),
          createdBy: cart.createdBy.email
        }));

        return dispatch(action.success({ accountNumber, carts }));
      })
      .catch(error => {
        const errorMsg = new TranslatedCopy({ id: 'global.toast.systemError' }).toString();
        dispatch(showToast({ message: errorMsg, type: 'error', error }));

        return dispatch(action.error(error?.message));
      });
  }
});

/**
 * @name getCartProducts
 * @async
 * @desc request cart products
 * @param {object} param
 * @param {string} cartId the id of the cart to fetch products from
 */
export const getCartProducts = asyncAction({
  baseName: 'GET_CART_PRODUCTS',
  action: action => ({ cartId }) => dispatch => {
    dispatch(action.start());
    return http
      .get(join(api2, `/carts/${cartId}/products?count=1000`))
      .then(({ data: response, httpMeta }) => {
        dispatch(fetchProductInfo({ styleNumbers: getProductsStyleCodes(response.objects || []) })).then(() => {
          dispatch(updateCartProductPurchasables());
          dispatch(sortAndFilterCart());
          dispatch(setCurrentCartTotals());
          dispatch(getCustomAddressForCurrentCart());
        });
        return dispatch(action.success({ products: response.objects, httpMeta }));
      })
      .catch(error => dispatch(action.error(error)));
  }
});

export const createOrGetCart = asyncAction({
  baseName: 'CREATE_OR_GET_CART',
  action: action => () => (dispatch, getState) => {
    const state = getState();
    const accountNumber = +getUserAccount(state).number;
    const selectedDepartment = getSelectedDepartment(state);
    const departmentNumber = +selectedDepartment.number;
    const cartType = orderTypeToCartType(selectedDepartment.type);

    dispatch(action.start());
    return http
      .post(join(api2, '/carts'), { accountNumber, departmentNumber, cartType })
      .then(({ data: response, httpMeta }) => {
        if (httpMeta.status === 200) dispatch(getCartProducts({ cartId: response.id }));

        const { email: userEmail } = getUserData(getState());
        const cart = {
          ...response,
          accountNumber: String(response.accountNumber),
          createdBy: userEmail
        };
        return dispatch(action.success({ cart, httpMeta }));
      })
      .catch(error => dispatch(action.error(error)));
  }
});

export const createCart = () => dispatch => dispatch(createOrGetCart());

export const addProducts = (products = []) => async (dispatch, getState) => {
  const state = getState();
  let cartId = getCurrentCart(state).id;
  const account = getUserAccount(state);

  // If no cart exists, create one.
  if (!cartId) {
    const createCartResponse = await dispatch(createCart());
    if (createCartResponse?.type === createOrGetCart.event.error) return Promise.resolve();

    cartId = await getCurrentCart(getState()).id; // Get the newly created cart ID
  }

  const requestList = products.map(product =>
    dispatch(
      addProductsRequest({
        cartId,
        product,
        accountNumber: account.number,
        ...(product.paymentMethod && { paymentMethod: product.paymentMethod }),
        ...(product.addressId && { addressId: product.addressId })
      })
    )
  );

  return Promise.all(requestList)
    .then(requests => {
      const hasError = requests.some(({ type }) => type === ADD_TO_CART_ERROR_MESSAGE);
      const toast = {
        message: new TranslatedCopy({ id: hasError ? 'shop.cart.addedError' : 'shop.cart.added' }).toString(),
        type: hasError ? 'error' : 'success'
      };

      dispatch(showToast(toast));
      dispatch(updateAccountCart({ cartId })); // TODO: do we still need this?

      return requests;
    })
    .catch(err => console.error('addProducts error:', err.message));
};

export const quickAddProductsBatchRequest = asyncAction({
  baseName: 'QUICK_ADD_TO_CART_BATCH',
  action: action => ({ cartId, products, accountNumber }) => dispatch => {
    dispatch(action.start(cartId));

    const productSizes = {};
    let dependsOn = [];
    const requests = products.map(product => {
      const sizes = product.sizes
        ? product.sizes
            .map(({ code = null, quantity = null }) => {
              if (code && quantity) {
                return {
                  sizeCode: code,
                  quantity: +quantity
                };
              }
              return null;
            })
            .filter(v => v)
        : [];

      productSizes[product.productCode] = sizes;

      const body = {
        productCode: product.productCode,
        paymentMethod: product.paymentMethod || defaultPaymentMethod.server,
        fundamentalRuleId: product.fundamentalRuleId,
        ...(sizes.length > 0 && { sizes }),
        ...(product.addressId && { addressId: product.addressId })
      };

      const request = {
        id: product.id,
        method: 'POST',
        url: `/v1/carts/${cartId}/products`,
        body,
        dependsOn
      };

      dependsOn = [product.id];

      return request;
    });

    let hasError = false;
    return http
      .post(`${api2}/$batch`, { requests })
      .then(({ data: { responses }, httpMeta }) => {
        const styleNumbers = [];
        const normalizedProducts = responses.reduce((returnedProducts, { status, body }) => {
          const requestSuccess = status === 200;
          hasError = hasError || !requestSuccess;

          if (hasError) {
            throw new Error('Error while adding product to cart', { status, body });
          }

          if (requestSuccess) {
            styleNumbers.push(body.productCode.split('-')[0]);
            returnedProducts.push({
              ...DEFAULT_CART_PRODUCT_STRUCTURE,
              fundamentalRuleId: body.fundamentalRuleId,
              productCode: body.productCode,
              isPurchasable: true,
              id: body.id,
              customerRequestDate: body.customerRequestDate,
              wholesalePrice: body.wholesalePrice || 0,
              retailPrice: body.retailPrice || 0,
              paymentMethod: defaultPaymentMethod.frontend,
              ...(productSizes[body.productCode].length > 0 && { sizes: productSizes[body.productCode] })
            });
          }
          return returnedProducts;
        }, []);
        return dispatch(fetchProductInfo({ styleNumbers })).then(() => {
          dispatch(action.success({ accountNumber, cartId, products: normalizedProducts, httpMeta }));
        });
      })
      .catch(error => {
        dispatch(
          showToast({
            message: new TranslatedCopy({ id: 'shop.cart.addedError' }).toString(),
            type: 'error'
          })
        );
        return dispatch(action.error(error));
      });
  }
});

export const quickAddProducts = (products = []) => async (dispatch, getState) => {
  const state = getState();
  let cartId = getCurrentCart(state).id;
  const accountNumber = getUserAccount(state).number;

  // If no cart exists, create one.
  if (!cartId) {
    const createCartResponse = await dispatch(createCart());
    if (createCartResponse?.type === createOrGetCart.event.error) return Promise.resolve();

    cartId = await getCurrentCart(getState()).id; // Get the newly created cart ID
  }

  // The following block is like this in other actions so i put it here
  /* eslint-disable no-unused-vars */
  const etag = getCurrentCartEtag(state);
  const headers = {
    // ...(etag && { 'If-Match': etag })
  };

  const productPromiseBatch = [];
  let productPosition = 0;

  while (productPosition < products.length) {
    const productOf20 = products.slice(productPosition, productPosition + 20);
    productPromiseBatch.push(dispatch(quickAddProductsBatchRequest({ cartId, products: productOf20, accountNumber })));
    productPosition += 20;
  }

  return Promise.all(productPromiseBatch).then(() => {
    dispatch(
      showToast({
        message: new TranslatedCopy({ id: 'shop.cart.added' }).toString(),
        type: 'success'
      })
    );
    return dispatch(updateAccountCart({ cartId }));
  });
};

export const getCartById = asyncAction({
  baseName: 'GET_CART_BY_ID',
  action: action => ({ cartId }) => dispatch => {
    dispatch(action.start());

    return http
      .get(createApiSearchUrl('carts', { id: cartId }))
      .then(({ data: response }) => {
        const cart = response.objects.map(cart => ({
          ...cart,
          accountNumber: String(cart.accountNumber),
          createdBy: cart.createdBy.email
        }))[0];
        dispatch(action.success({ cart }));
        dispatch(getCartProducts({ cartId: cartId }));
      })
      .catch(err => dispatch(action.error(err)));
  }
});

/**
 * @name updateCartProduct
 * @async
 * @desc request update to products in cart
 * @param {object} params
 * @param {string} cartId the id of the cart to be updated
 * @param {array} productUpdates array of objects containing productIds and corresponding fields to update
 */
export const updateCartProduct = asyncAction({
  baseName: 'UPDATE_CART_PRODUCTS',
  action: action => ({ cartId, product: { productId, updateFields: payload } }) => (dispatch, getState) => {
    dispatch(action.start({ cartId, productId, payload }));
    /* eslint-disable no-unused-vars */
    const state = getState();
    const etag = getCurrentCartEtag(state);
    const headers = {
      // ...(etag && { 'If-Match': etag })
    };
    /* eslint-enable no-unused-vars */

    return http
      .patch(join(api2, `carts/${cartId}/products/${productId}`), payload, headers)
      .then(({ httpMeta }) => dispatch(action.success({ httpMeta })))
      .catch(error => {
        dispatch(
          showToast({
            message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
            type: 'error',
            error
          })
        );

        return dispatch(action.error(error));
      });
  }
});

/**
 * @name submitCartRequest
 * @async
 * @desc submits a cart
 * @param {object} params
 * @param {string} cartId the id of the cart to be updated
 */
export const submitCartRequest = asyncAction({
  baseName: 'SUBMIT_CART',
  action: action => ({ cartId }) => (dispatch, getState) => {
    const state = getState();
    const { number: accountNumber } = getUserAccount(state);
    const etag = getCurrentCartEtag(state);
    const headers = { 'If-Match': etag };

    dispatch(action.start(cartId));

    return http
      .post(join(api2, `carts/${cartId}/submit`), null, headers)
      .then(({ data: { cartStatus }, httpMeta }) =>
        dispatch(action.success({ accountNumber, cartId, cartStatus, etag, httpMeta }))
      )
      .catch(error => {
        dispatch(
          showToast({
            message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
            type: 'error',
            error
          })
        );
        return dispatch(action.error(error));
      });
  }
});

export const saveCartProducts = ({ cartId, products = [], productIds }) => (dispatch, getState) => {
  let fetchedProducts = products;

  if (productIds) {
    const currentCart = getCurrentCart(getState());
    fetchedProducts = currentCart.products.filter(product => productIds.includes(product.id));
  }
  const productRequests = fetchedProducts.map(product => {
    const currentProduct = {
      productId: product.id,
      updateFields: {
        productCode: product.productCode,
        fundamentalRuleId: product.fundamentalRuleId,
        paymentMethod: paymentMethodsMap.get(product.paymentMethod),
        sizes: product.sizes.filter(item => item.quantity > 0)
      }
    };

    return dispatch(updateCartProduct({ cartId, product: currentProduct }));
  });

  return Promise.all(productRequests);
};

export const syncProductAddresses = () => (dispatch, getState) => {
  const state = getState();
  const { id: cartId, products, productGroups } = getCurrentCart(state);

  const productUpdates = [];

  productGroups.forEach(productGroup => {
    const { address, cartProductIds: productIds } = productGroup;
    const { addressId, addressType } = getProductAddressInfo(address);

    productIds.forEach(productId => {
      const cartProduct = products.find(product => product.id === productId);
      if (cartProduct.addressId !== addressId) {
        productUpdates.push({ productId, updateFields: { addressId, addressType } });
      }
    });
  });

  if (!productUpdates.length) {
    return Promise.resolve();
  }

  const actions = productUpdates.map(({ productId, updateFields: { addressId, addressType } }) =>
    dispatch(setProductAddress({ productId, addressId, addressType }))
  );

  return Promise.all(actions).then(() => {
    const productRequests = productUpdates.map(product => dispatch(updateCartProduct({ cartId, product })));
    return Promise.all(productRequests);
  });
};

export const setProductAddressesByGroupType = ({ address, groupType }) => (dispatch, getState) => {
  const state = getState();
  const { cartProductIds: productIds } = getProductGroupByType(state, groupType);
  const { addressId, addressType } = getProductAddressInfo(address);
  const actions = productIds.map(productId => dispatch(setProductAddress({ productId, addressId, addressType })));

  return Promise.all(actions);
};

export const saveProductAddressesByGroupType = ({ address, groupType }) => (dispatch, getState) => {
  const state = getState();
  const { id: cartId } = getCurrentCart(state);
  const { cartProductIds: productIds } = getProductGroupByType(state, groupType);
  const { addressId, addressType } = getProductAddressInfo(address);
  const productUpdates = productIds.map(productId => ({ productId, updateFields: { addressId, addressType } }));

  const productRequests = productUpdates.map(product => dispatch(updateCartProduct({ cartId, product })));
  return Promise.all(productRequests);
};

/**
 * @name updateCart
 * @async
 * @desc request update for cart
 * @param {object} params
 * @param {string} cartId the id of the cart to be updated
 * @param {object} params URL parameters (update fields)
 */
export const updateCart = asyncAction({
  baseName: 'UPDATE_CART',
  action: action => ({ cartId, cartUpdates }) => dispatch => {
    dispatch(action.start(cartId));

    return http
      .patch(join(api2, `carts/${cartId}`), cartUpdates)
      .then(({ httpMeta }) => dispatch(action.success({ cartId, httpMeta })))
      .catch(error => {
        dispatch(
          showToast({
            message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
            type: 'error',
            error
          })
        );
        return dispatch(action.error(error?.message));
      });
  }
});

export const saveCart = () => (dispatch, getState) => {
  const state = getState();
  const {
    id: cartId,
    headers: { shippingMethod, purchaseOrderNumber, phoneNumber, phoneNumberExt, notesToNike }
  } = getCurrentCart(state);
  const cartUpdates = {
    contactPhoneNumber: phoneNumber,
    contactExtension: phoneNumberExt,
    purchaseOrderNumber,
    shippingMethod: shippingMethodsMap.get(shippingMethod),
    userShippingInstructions: notesToNike
  };

  return dispatch(updateCart({ cartId, cartUpdates }));
};

export const refreshCart = asyncAction({
  baseName: 'REFRESH_CART',
  action: action => () => (dispatch, getState) => {
    dispatch(action.start());

    const state = getState();
    const { id: cartId } = getCurrentCart(state);

    return http
      .get(join(api2, `carts/${cartId}`))
      .then(async ({ data: cart, httpMeta }) => {
        dispatch(action.success({ cart, httpMeta }));
        dispatch(getCartProducts({ cartId: cartId }));
      })
      .catch(err => dispatch(action.error(err)));
  }
});

export const getCart = () => (dispatch, getState) => {
  const state = getState();
  const requestIsPending = cartIsLoading(state, [
    refreshCart.event.name,
    createOrGetCart.event.name,
    getCartById.event.name
  ]);

  if (!requestIsPending) {
    return dispatch(createOrGetCart());
  }

  return Promise.resolve();
};

export const showCartRefreshModal = () => dispatch => {
  dispatch(
    openModal({
      type: 'alert',
      title: new TranslatedCopy({ id: 'cart.modal.refresh.title' }).toString(),
      confirmAction: () => {
        dispatch(refreshCart()).then(() => {
          dispatch(closeModal());
        });
      },
      confirmText: new TranslatedCopy({ id: 'cart.modal.refresh.button' }).toString(),
      disabled: false
    })
  );
};

/**
 * @function deleteCartProductRequest
 * @async
 * @description dispatch delete action for product
 * @param {object} params
 * @param {string} params.cartId the current cart id
 * @param {string} params.cartProductId the id of the product to be deleted
 */
export const deleteCartProductRequest = asyncAction({
  baseName: 'DELETE_CART_PRODUCT',
  action: action => ({ cartId, cartProductId }) => (dispatch, getState) => {
    dispatch(action.start({ cartProductId }));
    /* eslint-disable no-unused-vars */
    const state = getState();
    const etag = getCurrentCartEtag(state);
    const headers = {
      // ...(etag && { 'If-Match': etag })
    };
    /* eslint-enable no-unused-vars */

    return http
      .delete(join(api2, `/carts/${cartId}/products/${cartProductId}`), null, headers)
      .then(({ httpMeta }) => dispatch(action.success({ cartProductId, httpMeta })))
      .catch(error => {
        dispatch(
          showToast({
            message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
            type: 'error',
            error
          })
        );

        return dispatch(action.error({ ...error, cartProductId }));
      });
  }
});

export const saveCurrentCartProducts = productIds => (dispatch, getState) => {
  const state = getState();
  const cart = getCurrentCart(state);
  const products = cart.products
    .filter(product => productIds.includes(product.id))
    .map(product => {
      const productInfo = getProductInfo(state, product.productCode);
      return {
        ...product,
        isDirectShip: productInfo?.directShip || false
      };
    });

  return dispatch(saveCartProducts({ cartId: cart.id, products }));
};

export const deleteCartProducts = productIds => (dispatch, getState) => {
  const state = getState();
  const cartId = getCurrentCart(state).id;
  const requestList = [];

  productIds.forEach(cartProductId => {
    requestList.push(
      dispatch(
        deleteCartProductRequest({
          cartId,
          cartProductId
        })
      )
    );
  });

  return Promise.all(requestList).then(deleteEvent => {
    dispatch(updateAccountCart({ cartId }));
    return deleteEvent;
  });
};

export const deleteCartProductById = productId => dispatch => dispatch(deleteCartProducts([productId]));

export const duplicateCartProducts = productIds => (dispatch, getState) => {
  const state = getState();
  const cart = getCurrentCart(state);
  const account = getUserAccount(state);
  const products = cart.products.filter(product => productIds.includes(product.id));

  const requestsList = [];
  products.forEach(prod =>
    requestsList.push(
      dispatch(
        duplicateCartProductsRequest({
          cartId: cart.id,
          product: prod,
          accountNumber: account.number,
          paymentMethod: paymentTypeToCartProductPaymentType(prod.paymentMethod)
        })
      )
    )
  );

  return Promise.all(requestsList).then(req => req);
};

export const duplicateCartProductById = productId => dispatch => dispatch(duplicateCartProducts([productId]));

// breaks up requestCurrentCartProductsDetails into multiple requests if too many productCodes for URL query params
const fetchAllCurrentCartProductsDetails = async ({
  accountNumber,
  departmentNumber,
  productCodes,
  languageCode,
  cartType
}) => {
  const url = join(
    api2,
    'products',
    `?filter=accountNumber(${accountNumber})`,
    `&filter=departmentNumber(${departmentNumber})`,
    `&filter=languageCodes(${languageCode})`,
    `&filter=cartType(${cartType})`,
    '&filter=realTime(true)'
  );

  const numberOfRequests = Math.ceil(productCodes.length / 500);

  const subsequentRequests = [];
  let productCodeStartIndex = 0;
  let productCodeEndIndex = 500;
  /* eslint-disable no-await-in-loop */
  for (let i = 0; i < numberOfRequests; i += 6) {
    const productCodeSet = productCodes.slice(productCodeStartIndex, productCodeEndIndex);
    const subsequestRequestsGroup = [];
    const limit = Math.min(6, numberOfRequests - i);
    for (let j = 0; j < limit; j++) {
      subsequestRequestsGroup.push(http.get(`${url}&filter=productCode(${productCodeSet.join(',')})`));
      productCodeStartIndex += 500;
      productCodeEndIndex += 500;
    }

    await Promise.all(subsequestRequestsGroup)
      .then(responses => responses.map(r => r.data.objects))
      .then(result => subsequentRequests.push(flatten(result)));
  }

  return flatten(subsequentRequests);
};

export const requestCurrentCartProductsDetails = asyncAction({
  baseName: 'REQUEST_CART_PRODUCT_DETAILS',
  action: action => values => (dispatch, getState) => {
    dispatch(action.start(values));

    const state = getState();
    const {
      headers: { cartType, departmentNumber, languageCode },
      products
    } = getCurrentCart(state);
    const accountNumber = getUserAccount(state).number;

    const productCodes = products.map(p => p.productCode);
    return fetchAllCurrentCartProductsDetails({
      accountNumber,
      departmentNumber,
      productCodes,
      languageCode: languageCode.toUpperCase(),
      cartType
    })
      .then(response => dispatch(action.success(response)))
      .catch(error => dispatch(action.error({ error })));
  }
});

/**
 * Sets the selected department based on it's number and order type. Additionally,
 * it will set the user's current cart or the cart of the provided cartId
 * @param number
 * @param orderType
 * @param cartId
 * @returns {function(...[*]=)}
 */
export const setSelectedDepartment = (number, orderType, cartId = '') => (dispatch, getState) => {
  const state = getState();
  const [currentAllotments, futureAllotments] = [getCurrentAllotments(state), getFutureAllotments(state)];
  const currDept = [
    ...mapAllotmentDepts(currentAllotments?.departments),
    ...mapAllotmentDepts(futureAllotments?.departments)
  ].find(({ departmentNumber }) => departmentNumber === number);
  const futureMatch = futureAllotments.departments && futureAllotments.departments[currDept.departmentNumber];

  dispatch(
    selectShopDepartment({
      type: orderType,
      name: currDept.departmentName,
      number: currDept.departmentNumber,
      code: currDept.currencyCode,
      currentRemaining: currDept ? currDept.remainingAllotment : ZERO_DOLLAR_VALUE,
      futureRemaining: futureMatch ? futureMatch.remainingAllotment : ZERO_DOLLAR_VALUE,
      purchaseOptions: currDept.purchaseOptions,
      unitAllotments: currDept.unitAllotments,
      currentBillable: currDept && currDept.billable,
      futureBillable: futureMatch && futureMatch.billable,
      poPrefix: futureMatch ? futureMatch.poPrefix : null
    })
  );

  if (cartId) {
    dispatch(getCartById({ cartId }));
  } else {
    dispatch(getCart());
  }
};

export const toggleSelectedCartProducts = createAction('SELECT_CART_PRODUCTS');

export const toggleSelectedCartProductById = productId => dispatch =>
  dispatch(toggleSelectedCartProducts({ productIds: [productId] }));

export const deselectAllCartProducts = () => (dispatch, getState) => {
  const selectedCartProducts = getCurrentCart(getState())
    .products.filter(product => product.isSelected)
    .map(product => product.id);
  return dispatch(toggleSelectedCartProducts({ productIds: selectedCartProducts }));
};

export const selectAllCartProducts = () => (dispatch, getState) => {
  const unselectedCartProducts = getCurrentCart(getState())
    .products.filter(product => !product.isSelected)
    .map(product => product.id);
  return dispatch(toggleSelectedCartProducts({ productIds: unselectedCartProducts }));
};

export const deleteSelectedCartProducts = () => (dispatch, getState) => {
  const selectedCartProducts = getCurrentCart(getState())
    .products.filter(product => product.isSelected)
    .map(product => product.id);
  return dispatch(deleteCartProducts(selectedCartProducts));
};

export const duplicateSelectedCartProducts = () => (dispatch, getState) => {
  const selectedCartProducts = getCurrentCart(getState())
    .products.filter(product => product.isSelected)
    .map(product => product.id);
  return dispatch(duplicateCartProducts(selectedCartProducts));
};

export const assignPaymentMethodToSelectedCartProducts = method => async (dispatch, getState) => {
  const currentCart = getCurrentCart(getState());
  const productIds = currentCart.products.filter(product => product.isSelected).map(product => product.id);

  await dispatch(setPaymentMethod({ productIds, method }));
  return dispatch(saveCartProducts({ cartId: currentCart.id, productIds }));
};

export const submitCartForApproval = asyncAction({
  baseName: 'SUBMIT_CART_FOR_APPROVAL',
  action: action => cartId => dispatch => {
    dispatch(action.start(cartId));
    return http
      .put(join(api, 'cart/submit/approval'), { cartId })
      .then(({ data: response }) => dispatch(action.success(response)))
      .catch(error => {
        dispatch(
          showToast({
            message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
            type: 'error',
            error
          })
        );
        dispatch(action.error(error?.message));
      });
  }
});

export const approveCartRequest = asyncAction({
  baseName: 'APPROVE_CART',
  action: action => cartId => dispatch => {
    dispatch(action.start(cartId));
    return http
      .put(join(api, 'cart/approve/submit'), { cartId })
      .then(({ data: response }) => {
        dispatch(hasSubmittedToggle());
        return dispatch(action.success(response));
      })
      .catch(error => {
        dispatch(
          showToast({
            message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
            type: 'error',
            error
          })
        );
        return dispatch(action.error(error?.message));
      });
  }
});

/**
 * @name submitOrder
 * @async
 * @desc submit order
 * @usage checkout order review
 */
export const submitOrder = () => async (dispatch, getState) => {
  const state = getState();
  const {
    headers: { orderType },
    id: cartId
  } = getCurrentCart(state);
  const { number: accountNumber } = getUserAccount(state);

  await dispatch(fetchGamedayAccount({ accountNumber })); // refresh allotments

  if (orderType === ORDER_TYPE_ATONCE) {
    await dispatch(requestCurrentCartProductsDetails()); // refresh availability
  }

  await dispatch(validateCart());

  if (hasErrors(getState())) {
    dispatch(showToast({ message: new TranslatedCopy({ id: 'checkout.error.submit' }).toString(), type: 'error' }));

    return dispatch(push('/cart'));
  }

  const event = await dispatch(submitCartRequest({ cartId }));
  if (event.type === submitCartRequest.event.success) dispatch(push('/order-confirmation'));
  else if (event.type === submitCartRequest.event.error && event.payload?.status === 412) {
    throw new Error('etag mismatch error on cart submit', { cause: event.payload.errorMessage });
  }

  return event;
};

export const rejectCartRequest = asyncAction({
  baseName: 'REJECT_CART',
  action: action => ({ cartId, accountNumber }) => dispatch => {
    dispatch(action.start(cartId));
    return http
      .post(join(api2, `carts/${cartId}/reject`))
      .then(() => {
        dispatch(
          showToast({
            message: new TranslatedCopy({ id: 'checkout.review.reject.toast' }).toString(),
            type: 'success'
          })
        );
        return dispatch(action.success({ cartId, accountNumber }));
      })
      .catch(error => {
        dispatch(
          showToast({
            message: new TranslatedCopy({ id: 'global.toast.systemError' }).toString(),
            type: 'error',
            error
          })
        );
        return dispatch(action.error(error));
      });
  }
});

export const rejectOrder = () => (dispatch, getState) => {
  const state = getState();
  const account = getUserAccount(state);
  const currentCart = getCurrentCart(state);

  return dispatch(rejectCartRequest({ cartId: currentCart.id, accountNumber: account.number }));
};

const getRequestName = func => (func ? (func.event && func.event.name) || func.toString() : '');

// All asynchronous cart requests should get included here
export const CART_REQUESTS = {
  EVENT_CART_APPROVE: getRequestName(approveCartRequest),
  EVENT_CART_CREATE: getRequestName(createOrGetCart),
  EVENT_CART_BY_ID: getRequestName(getCartById),
  EVENT_GET_ALL_ACCOUNT_CARTS: getRequestName(getAllAccountCarts),
  EVENT_DELETE_CART_PRODUCT_REQUEST: getRequestName(deleteCartProductRequest),
  EVENT_UPDATE_CART_PRODUCTS: getRequestName(updateCartProduct),
  EVENT_UPDATE_ACCOUNT_CART: getRequestName(updateAccountCart),
  EVENT_ADD_PRODUCT_REQUEST: getRequestName(addProductsRequest),
  EVENT_QUICK_ADD_PRODUCTS_BATCH_REQUEST: getRequestName(quickAddProductsBatchRequest),
  EVENT_GET_CART_PRODUCTS: getRequestName(getCartProducts),
  EVENT_DUPLICATE_CART_PRODUCTS_REQUEST: getRequestName(duplicateCartProductsRequest),
  EVENT_CART_SUBMIT: getRequestName(submitCartRequest)
};

// Request grouped-accessors
export const EVENT_GROUP_ALL = [...Object.values(CART_REQUESTS)].filter(val => val && val !== '');
export const EVENT_GROUP_REQUEST_CARTS = [
  CART_REQUESTS.EVENT_CART_CREATE,
  CART_REQUESTS.EVENT_CART_BY_ID,
  CART_REQUESTS.EVENT_GET_ALL_ACCOUNT_CARTS,
  CART_REQUESTS.EVENT_GET_CART_PRODUCTS
];
export const EVENT_GROUP_ADD_REMOVE = [
  CART_REQUESTS.EVENT_DELETE_CART_PRODUCT_REQUEST,
  CART_REQUESTS.EVENT_UPDATE_ACCOUNT_CART,
  CART_REQUESTS.EVENT_ADD_PRODUCT_REQUEST,
  CART_REQUESTS.EVENT_QUICK_ADD_PRODUCTS_BATCH_REQUEST,
  CART_REQUESTS.EVENT_UPDATE_CART_PRODUCTS,
  CART_REQUESTS.EVENT_DUPLICATE_CART_PRODUCTS_REQUEST
];
export const EVENT_GROUP_SUBMIT_CART = [
  CART_REQUESTS.EVENT_CART_APPROVE,
  CART_REQUESTS.EVENT_CART_SUBMIT,
  CART_REQUESTS.EVENT_CART_BY_ID
];
export const EVENT_GROUP_ADD_TO_CART = [
  CART_REQUESTS.EVENT_ADD_PRODUCT_REQUEST,
  CART_REQUESTS.EVENT_QUICK_ADD_PRODUCTS_BATCH_REQUEST,
  CART_REQUESTS.EVENT_CART_BY_ID,
  CART_REQUESTS.EVENT_CART_CREATE
];
